import { useState, type FormEvent, type ReactElement } from 'react';
import { ApiError } from '@/lib/api';
import {
  GenerateRecipeRequestSchema,
  NutritionBudgetExceededError,
  NutritionNotConfiguredError,
  NutritionUpstreamError,
  useGenerateRecipe,
  type GenerateRecipeRequest,
} from '@/lib/nutrition';

/**
 * Formulario inline para generar una receta nueva con IA.
 *
 * Se usa dentro de un modal en `RecipesView`. Mismas convenciones que
 * `GenerateMealPlanForm`: validación con el mismo Zod schema que valida el
 * backend, spinner mientras isPending, mapeo de errores Anthropic-específicos.
 */
export function GenerateRecipeForm({
  onSuccess,
  onCancel,
}: {
  onSuccess?: () => void;
  onCancel?: () => void;
}): ReactElement {
  const mutation = useGenerateRecipe();

  const [title, setTitle] = useState('');
  const [servings, setServings] = useState<string>('1');
  const [dietRestrictions, setDietRestrictions] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  function buildRequest(): GenerateRecipeRequest {
    const body: GenerateRecipeRequest = {
      title: title.trim(),
    };
    const servingsNum = Number(servings);
    if (Number.isFinite(servingsNum) && servingsNum > 0) {
      body.servings = Math.round(servingsNum);
    }
    const trimmedDiet = dietRestrictions.trim();
    if (trimmedDiet) body.dietRestrictions = trimmedDiet;
    const trimmedNotes = additionalNotes.trim();
    if (trimmedNotes) body.additionalNotes = trimmedNotes;
    return body;
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (mutation.isPending) return;
    const candidate = buildRequest();
    const parsed = GenerateRecipeRequestSchema.safeParse(candidate);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? '');
        if (key && !next[key]) next[key] = issue.message;
      }
      setFieldErrors(next);
      return;
    }
    setFieldErrors({});
    mutation.mutate(parsed.data, {
      onSuccess: () => {
        if (onSuccess) onSuccess();
      },
    });
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div>
        <label htmlFor="recipe-title" className="block text-sm font-medium text-slate-700">
          Título de la receta
        </label>
        <input
          id="recipe-title"
          type="text"
          required
          maxLength={200}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ej: bowl de quinoa con pollo y aguacate"
          aria-invalid={fieldErrors.title !== undefined}
          aria-describedby={fieldErrors.title ? 'recipe-title-error' : undefined}
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        />
        {fieldErrors.title && (
          <p id="recipe-title-error" role="alert" className="mt-1 text-xs text-rose-700">
            {fieldErrors.title}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="recipe-servings" className="block text-sm font-medium text-slate-700">
          Raciones
        </label>
        <input
          id="recipe-servings"
          type="number"
          min={1}
          max={50}
          inputMode="numeric"
          value={servings}
          onChange={(e) => setServings(e.target.value)}
          aria-invalid={fieldErrors.servings !== undefined}
          className="mt-1 block w-24 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        />
      </div>

      <div>
        <label htmlFor="recipe-diet" className="block text-sm font-medium text-slate-700">
          Restricciones dietéticas (opcional)
        </label>
        <input
          id="recipe-diet"
          type="text"
          maxLength={500}
          value={dietRestrictions}
          onChange={(e) => setDietRestrictions(e.target.value)}
          placeholder="Ej: vegetariano, sin gluten"
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        />
      </div>

      <div>
        <label htmlFor="recipe-notes" className="block text-sm font-medium text-slate-700">
          Notas adicionales (opcional)
        </label>
        <textarea
          id="recipe-notes"
          rows={3}
          maxLength={1000}
          value={additionalNotes}
          onChange={(e) => setAdditionalNotes(e.target.value)}
          placeholder="Ej: para pre-entreno, máximo 30 min de preparación"
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        />
        <p className="mt-1 text-xs text-slate-500">
          {additionalNotes.length} / 1000 caracteres
        </p>
      </div>

      {mutation.isPending && (
        <div
          role="status"
          aria-live="polite"
          aria-busy="true"
          className="flex items-center gap-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          <Spinner />
          <span>Generando receta…</span>
        </div>
      )}

      {mutation.isError && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {mapRecipeError(mutation.error)}
        </p>
      )}

      <div className="flex items-center justify-end gap-3">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={mutation.isPending}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancelar
          </button>
        )}
        <button
          type="submit"
          disabled={mutation.isPending}
          aria-busy={mutation.isPending}
          className="rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? (
            <span className="inline-flex items-center gap-2">
              <Spinner /> Generando…
            </span>
          ) : (
            'Generar receta'
          )}
        </button>
      </div>
    </form>
  );
}

function Spinner(): ReactElement {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}

function mapRecipeError(err: Error): string {
  if (err instanceof NutritionBudgetExceededError) {
    return (
      err.hint ||
      'Has alcanzado tu presupuesto mensual de Anthropic. Súbelo en /ajustes/anthropic o espera al mes siguiente.'
    );
  }
  if (err instanceof NutritionNotConfiguredError) {
    return `Falta configurar tu ANTHROPIC_API_KEY. Configúrala en /ajustes/anthropic.${
      err.hint ? ` ${err.hint}` : ''
    }`;
  }
  if (err instanceof NutritionUpstreamError) {
    return 'Error de Anthropic, vuelve a intentar en un momento.';
  }
  if (err instanceof ApiError && err.status === 422) {
    return `La receta generada no cumple el esquema esperado. ${err.message ?? ''}`.trim();
  }
  return `No se pudo crear la receta. ${err.message ?? ''}`.trim();
}
